/**
 * Fractional ordering keys for blocks and pages.
 *
 * Generates strings that sort lexicographically, allowing insertion between
 * two elements without reindexing the rest — critical with PowerSync, where
 * every UPDATE is a sync operation: moving a block costs 1 row, not N.
 */

const ALPHABET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
const BASE = ALPHABET.length;
const MAX_DIGIT = BASE - 1;

function digitAt(key: string, index: number): number {
  if (index >= key.length) {
    return 0;
  }
  const digit = ALPHABET.indexOf(key.charAt(index));
  if (digit === -1) {
    throw new Error(`Invalid sort key character: ${key.charAt(index)}`);
  }
  return digit;
}

/**
 * String strictly between `lo` and `hi` (lexicographic).
 * `hi === ""` represents +infinity. Precondition: lo < hi (or hi empty).
 */
function midpoint(lo: string, hi: string): string {
  let prefix = "";
  let i = 0;
  for (;;) {
    const loDigit = digitAt(lo, i);
    const hiDigit = i < hi.length ? digitAt(hi, i) : BASE;
    if (loDigit === hiDigit) {
      prefix += ALPHABET.charAt(loDigit);
      i += 1;
      continue;
    }
    if (hiDigit - loDigit > 1) {
      return prefix + ALPHABET.charAt(Math.floor((loDigit + hiDigit) / 2));
    }
    // Adjacent digits: descend to the next level looking for a gap above `lo`
    prefix += ALPHABET.charAt(loDigit);
    i += 1;
    for (;;) {
      const nextLoDigit = digitAt(lo, i);
      if (nextLoDigit < MAX_DIGIT) {
        return prefix + ALPHABET.charAt(Math.ceil((nextLoDigit + BASE) / 2));
      }
      prefix += ALPHABET.charAt(MAX_DIGIT);
      i += 1;
    }
  }
}

/**
 * Returns a key strictly between `before` and `after`.
 * `null` means no bound (start or end of the page).
 */
export function sortKeyBetween(before: string | null, after: string | null): string {
  const lo = before ?? "";
  const hi = after ?? "";
  if (lo !== "" && hi !== "" && lo >= hi) {
    throw new Error(`sortKeyBetween: invalid bounds "${lo}" >= "${hi}"`);
  }
  return midpoint(lo, hi);
}

/**
 * Generates `count` consecutive keys after `before` (for initial loads).
 */
export function sortKeySequence(count: number, before: string | null = null): string[] {
  const keys: string[] = [];
  let prev = before;
  for (let i = 0; i < count; i++) {
    const key = sortKeyBetween(prev, null);
    keys.push(key);
    prev = key;
  }
  return keys;
}
